import { Button, Card, Col, Form, Input, Row, Select, Space, Tooltip, TreeSelect, theme } from 'antd';
import { useEffect } from 'react';
import { DatePicker } from 'antd';
import { computeButtonColLayout, filterFormGridLayout, filterFormItemLayout, filterFormItemStyle } from '@utils/property';
import { filterTreeNodeByTitle } from '@/utils/antdTreeSelect';
import { expertLevels } from '@utils/constants';
import './GuestJourneyFilterPanel.less';

const VISIBLE_FIELD_COUNT = 8;

export default function GuestJourneyFilterPanel({ filter, conferenceHallTreeData, dateOptions = [], hotelOptions = [], hotelRoomTypeOptions = [], selectedHotelId, collapsed, onFinish, onReset, onToggleCollapsed, onHotelChange }) {
    const { token } = theme.useToken();
    const [form] = Form.useForm();
    const buttonColLayout = computeButtonColLayout(VISIBLE_FIELD_COUNT);

    useEffect(() => {
        form.setFieldsValue({
            name: filter.name,
            expertLevels: filter.expertLevels,
            hasTask: filter.hasTask,
            invitationAccepted: filter.invitationAccepted,
            agendaTime: filter.agendaTime,
            conferenceHallId: filter.conferenceHallId,
            taskBeginTime: filter.taskBeginTime,
            taskEndTime: filter.taskEndTime,
            arrivalTransportNeed: filter.arrivalTransportNeed,
            arrivalTransportInput: filter.arrivalTransportInput,
            returnTransportNeed: filter.returnTransportNeed,
            returnTransportInput: filter.returnTransportInput,
            arrivalCarNeed: filter.arrivalCarNeed,
            arrivalCarInput: filter.arrivalCarInput,
            returnCarNeed: filter.returnCarNeed,
            returnCarInput: filter.returnCarInput,
            hotelNeed: filter.hotelNeed,
            hotelInput: filter.hotelInput,
            authedTransport: filter.authedTransport,
            authedCar: filter.authedCar,
            authedHotel: filter.authedHotel,
            arrivalTransportTime: filter.arrivalTransportTime,
            returnTransportTime: filter.returnTransportTime,
            arrivalCarTime: filter.arrivalCarTime,
            returnCarTime: filter.returnCarTime,
            checkinDate: filter.checkinDate,
            checkoutDate: filter.checkoutDate,
            hotelId: filter.hotelId,
            hotelRoomTypeId: filter.hotelRoomTypeId,
        });
    }, [filter, form]);

    const handleReset = () => {
        form.resetFields();
        onReset();
    };

    return (
        <Card
            size="small"
            className="guest-journey-filter-panel"
            style={{
                marginBottom: token.marginLG,
                border: `1px solid ${token.colorBorderSecondary}`,
                background: token.colorFillAlter,
            }}
        >
            <Form {...filterFormItemLayout} form={form} onFinish={onFinish} autoComplete="off" requiredMark={false}>
                <Row gutter={[16, 16]}>
                    <Col {...filterFormGridLayout}>
                        <Form.Item label="嘉宾姓名" name="name" style={filterFormItemStyle}>
                            <Input allowClear placeholder="嘉宾姓名" />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="嘉宾级别" name="expertLevels" style={filterFormItemStyle}>
                            <Select mode="multiple" maxTagCount="responsive" options={expertLevels} fieldNames={{ label: 'label', value: 'level' }} placeholder="请选择嘉宾级别" allowClear />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="会议任务" name="hasTask" style={filterFormItemStyle}>
                            <Select
                                options={[
                                    { value: 1, label: '有会议任务' },
                                    { value: 0, label: '无会议任务' },
                                ]}
                                placeholder="请选择会议任务"
                                allowClear
                            />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="嘉宾任务状态" name="invitationAccepted" style={filterFormItemStyle}>
                            <Select
                                options={[
                                    { value: 0, label: '待确认' },
                                    { value: 1, label: '已确认(含部分确认)' },
                                    { value: 2, label: '已拒绝' },
                                ]}
                                placeholder="请选择嘉宾任务接受状态"
                                allowClear
                            />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item
                            label={
                                <span className="form-item-label-text">
                                    任务日期
                                    <Tooltip
                                        title={
                                            <span>
                                                任务开始时间 &lt; 12:00 为上午
                                                <br />
                                                任务开始时间 ≧ 12:00 为下午
                                            </span>
                                        }
                                    >
                                        <i className="bi bi-question-circle-fill" style={{ marginLeft: 4, color: '#faad14' }}></i>
                                    </Tooltip>
                                </span>
                            }
                            name="agendaTime"
                            style={filterFormItemStyle}
                        >
                            <Select options={dateOptions} placeholder="请选择日期" allowClear />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="任务所属会议" name="conferenceHallId" style={filterFormItemStyle}>
                            <TreeSelect showSearch={{ filterTreeNode: filterTreeNodeByTitle }} styles={{ popup: { root: { minWidth: 500, overflow: 'auto' } } }} allowClear treeDefaultExpandAll treeData={conferenceHallTreeData} placeholder="请选择会议" />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="任务开始时间" name="taskBeginTime" style={filterFormItemStyle}>
                            <DatePicker.RangePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder={['开始时间', '结束时间']} />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="任务结束时间" name="taskEndTime" style={filterFormItemStyle}>
                            <DatePicker.RangePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder={['开始时间', '结束时间']} />
                        </Form.Item>
                    </Col>

                    {!collapsed && (
                        <>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="来程交通需求" name="arrivalTransportNeed" style={filterFormItemStyle}>
                                    <Select
                                        options={[
                                            { value: 1, label: '会务组安排' },
                                            { value: 2, label: '自费' },
                                            { value: 0, label: '不需要' },
                                            { value: 3, label: '待确定' },
                                        ]}
                                        placeholder="请选择"
                                        allowClear
                                        mode="multiple"
                                    />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="来程交通录入" name="arrivalTransportInput" style={filterFormItemStyle}>
                                    <Select
                                        options={[
                                            { value: 1, label: '已录入' },
                                            { value: 0, label: '未录入' },
                                        ]}
                                        placeholder="请选择"
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="返程交通需求" name="returnTransportNeed" style={filterFormItemStyle}>
                                    <Select
                                        options={[
                                            { value: 1, label: '会务组安排' },
                                            { value: 2, label: '自费' },
                                            { value: 0, label: '不需要' },
                                            { value: 3, label: '待确定' },
                                        ]}
                                        placeholder="请选择"
                                        allowClear
                                        mode="multiple"
                                    />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="返程交通录入" name="returnTransportInput" style={filterFormItemStyle}>
                                    <Select
                                        options={[
                                            { value: 1, label: '已录入' },
                                            { value: 0, label: '未录入' },
                                        ]}
                                        placeholder="请选择"
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="来程用车需求" name="arrivalCarNeed" style={filterFormItemStyle}>
                                    <Select
                                        options={[
                                            { value: 1, label: '会务组安排' },
                                            { value: 2, label: '自费' },
                                            { value: 0, label: '不需要' },
                                            { value: 3, label: '待确定' },
                                        ]}
                                        placeholder="请选择"
                                        allowClear
                                        mode="multiple"
                                    />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="来程用车录入" name="arrivalCarInput" style={filterFormItemStyle}>
                                    <Select
                                        options={[
                                            { value: 1, label: '已录入' },
                                            { value: 0, label: '未录入' },
                                        ]}
                                        placeholder="请选择"
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="返程用车需求" name="returnCarNeed" style={filterFormItemStyle}>
                                    <Select
                                        options={[
                                            { value: 1, label: '会务组安排' },
                                            { value: 2, label: '自费' },
                                            { value: 0, label: '不需要' },
                                            { value: 3, label: '待确定' },
                                        ]}
                                        placeholder="请选择"
                                        allowClear
                                        mode="multiple"
                                    />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="返程用车录入" name="returnCarInput" style={filterFormItemStyle}>
                                    <Select
                                        options={[
                                            { value: 1, label: '已录入' },
                                            { value: 0, label: '未录入' },
                                        ]}
                                        placeholder="请选择"
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="住宿需求" name="hotelNeed" style={filterFormItemStyle}>
                                    <Select
                                        options={[
                                            { value: 1, label: '会务组安排' },
                                            { value: 2, label: '自费' },
                                            { value: 0, label: '不需要' },
                                            { value: 3, label: '待确定' },
                                        ]}
                                        placeholder="请选择"
                                        allowClear
                                        mode="multiple"
                                    />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="住宿录入" name="hotelInput" style={filterFormItemStyle}>
                                    <Select
                                        options={[
                                            { value: 1, label: '已录入' },
                                            { value: 0, label: '未录入' },
                                        ]}
                                        placeholder="请选择"
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="交通授权" name="authedTransport" style={filterFormItemStyle}>
                                    <Select
                                        options={[
                                            { value: 1, label: '已授权' },
                                            { value: 0, label: '未授权' },
                                        ]}
                                        placeholder="请选择"
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="用车授权" name="authedCar" style={filterFormItemStyle}>
                                    <Select
                                        options={[
                                            { value: 1, label: '已授权' },
                                            { value: 0, label: '未授权' },
                                        ]}
                                        placeholder="请选择"
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="住宿授权" name="authedHotel" style={filterFormItemStyle}>
                                    <Select
                                        options={[
                                            { value: 1, label: '已授权' },
                                            { value: 0, label: '未授权' },
                                        ]}
                                        placeholder="请选择"
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="来程交通时间" name="arrivalTransportTime" style={filterFormItemStyle}>
                                    <DatePicker.RangePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder={['开始时间', '结束时间']} />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="返程交通时间" name="returnTransportTime" style={filterFormItemStyle}>
                                    <DatePicker.RangePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder={['开始时间', '结束时间']} />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="来程用车时间" name="arrivalCarTime" style={filterFormItemStyle}>
                                    <DatePicker.RangePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder={['开始时间', '结束时间']} />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="返程用车时间" name="returnCarTime" style={filterFormItemStyle}>
                                    <DatePicker.RangePicker showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} placeholder={['开始时间', '结束时间']} />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="入住日期" name="checkinDate" style={filterFormItemStyle}>
                                    <DatePicker.RangePicker format="YYYY-MM-DD" style={{ width: '100%' }} placeholder={['开始日期', '结束日期']} />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="退房日期" name="checkoutDate" style={filterFormItemStyle}>
                                    <DatePicker.RangePicker format="YYYY-MM-DD" style={{ width: '100%' }} placeholder={['开始日期', '结束日期']} />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="酒店名称" name="hotelId" style={filterFormItemStyle}>
                                    <Select
                                        placeholder="请选择"
                                        options={hotelOptions}
                                        onChange={hotelId => {
                                            if (!hotelId) {
                                                form.setFieldValue('hotelRoomTypeId', undefined);
                                            }
                                            onHotelChange(hotelId);
                                        }}
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>

                            {selectedHotelId && (
                                <Col {...filterFormGridLayout}>
                                    <Form.Item label="酒店房型" name="hotelRoomTypeId" style={filterFormItemStyle}>
                                        <Select placeholder="请先选择酒店" options={hotelRoomTypeOptions} allowClear />
                                    </Form.Item>
                                </Col>
                            )}
                        </>
                    )}

                    <Col {...buttonColLayout}>
                        <Form.Item label={<span>&nbsp;</span>} colon={false} style={filterFormItemStyle}>
                            <Space size="small">
                                <Button type="primary" htmlType="submit" icon={<i className="bi bi-search"></i>}>
                                    检索
                                </Button>
                                <Button type="default" onClick={handleReset}>
                                    重置
                                </Button>
                                <Button type="link" size="small" onClick={onToggleCollapsed} style={{ fontSize: 12, color: token.colorTextSecondary }}>
                                    {!collapsed ? (
                                        <>
                                            收起 <i className="bi bi-chevron-up"></i>
                                        </>
                                    ) : (
                                        <>
                                            展开 <i className="bi bi-chevron-down"></i>
                                        </>
                                    )}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
}
